import React, { useState } from "react";
import { ComponentDefinition } from "@/types/models/componentConfig";
import { Section } from "@/types/models/page";
import { ChangeHistoryEntry } from "@/services/sectionDataService";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

interface DebugJsonPanelProps {
  isOpen: boolean;
  onClose: () => void;
  section: Section | null;
  history: ChangeHistoryEntry[];
  componentDefinitions: ComponentDefinition[];
}

export function DebugJsonPanel({
  isOpen,
  onClose,
  section,
  history,
  componentDefinitions,
}: DebugJsonPanelProps) {
  const [activeTab, setActiveTab] = useState<"section" | "history" | "config">(
    "section",
  );

  const renderContent = () => {
    switch (activeTab) {
      case "section":
        return (
          <pre className="text-xs font-mono whitespace-pre-wrap">
            {JSON.stringify(section, null, 2)}
          </pre>
        );
      case "history":
        return (
          <pre className="text-xs font-mono whitespace-pre-wrap">
            {JSON.stringify(history, null, 2)}
          </pre>
        );
      case "config":
        return (
          <pre className="text-xs font-mono whitespace-pre-wrap">
            {JSON.stringify(componentDefinitions, null, 2)}
          </pre>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card dark:bg-surface-elevated w-11/12 max-w-7xl h-5/6 rounded-lg shadow-2xl flex flex-col border border-border">
        <div className="p-4 border-b border-border flex justify-between items-center flex-shrink-0">
          <h3 className="font-semibold text-lg">Debug View</h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
        <div className="p-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            {(["section", "history", "config"] as const).map((tab) => (
              <Button
                key={tab}
                variant={activeTab === tab ? "default" : "outline"}
                onClick={() => setActiveTab(tab)}
                className="capitalize"
              >
                {tab}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex-1 p-4 overflow-auto">{renderContent()}</div>
      </div>
    </div>
  );
}
